/**
 * Follow a long subtree operation that runs as a background job — a big
 * folder delete today (#781), a big restore next (#854) — to its end.
 *
 * Nothing here knows which operation it follows: the caller names the `kind`
 * (the `operation:progress` events it listens to), how to ask the job's status
 * route, and how to read a finished job's result from an event. The per-kind
 * instances live beside their feature (`delete-tracker.ts`).
 *
 * ── THE WEBSOCKET IS THE CHANNEL; THE STATUS ROUTE IS THE SAFETY NET ──────
 * The job reports every phase, and its outcome, as `operation:progress`
 * events on the socket the tab already holds. That is the only thing this
 * reads while the socket is healthy — no polling.
 *
 * The status route is asked only when the socket cannot be trusted to deliver:
 *   - the socket is down: every `offlinePollMs` until it is back (a job keeps
 *     running while the tab is offline, and its outcome is not replayed);
 *   - the socket came back: once, because the outcome may have gone out in
 *     the gap (operation events carry no seq, so resync does not cover them);
 *   - nothing arrived for `stallMs` on an "open" socket: once, then re-armed
 *     (a half-open socket looks connected until its next ping fails);
 *   - following an earlier tab's job (a joined request, or a reload): once at
 *     the start, since its outcome may already be out;
 *   - a `completed` event came without its result: on every tick until the
 *     route's answer is final (the event can beat BullMQ's own completion).
 *
 * ── NO EVENT IS MISSED BEFORE THE 202 ──────────────────────────────────────
 * `createJobTracker` subscribes BEFORE the request is sent, keyed on the
 * client-minted operation id, and buffers. A job just over the async
 * threshold can finish before the 202 is read; its events are replayed when
 * `follow` binds the job id.
 */
import type { OperationProgressEvent } from '@papan/realtime/client'
import { JOB_DEADLINE_MS } from '@papan/types'
import { currentSessionSignal, isSessionEndedError, sessionEndedSince, SessionEndedError } from '@papan/api-client'

export type JobKind = NonNullable<OperationProgressEvent['kind']>

/** A job status route's answer. `failed` is a 200, not an error. */
export interface JobStatus<R> {
  state: 'queued' | 'running' | 'completed' | 'failed'
  result?: R
  error?: string
}

export interface JobProgress {
  phase: string
  done: number
  total: number
}

/** The job ended in failure. `message` is the server's, in plain words. */
export class JobFailedError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'JobFailedError'
  }
}

/**
 * The job is gone (its result is kept an hour) or was never seen again, so
 * the outcome is unknown. The listing is the truth now: refresh it.
 */
export class JobLostError extends Error {
  constructor() {
    super('The job finished while this page was away')
    this.name = 'JobLostError'
  }
}

export interface JobTrackerDeps<R> {
  kind: JobKind
  onOperationEvent: (handler: (event: OperationProgressEvent) => void) => () => void
  /** The socket's state right now (read on every tick, not captured once). */
  isConnected: () => boolean
  getStatus: (jobId: string) => Promise<JobStatus<R>>
  /** A `completed` event's result, or null when the event does not carry one. */
  readResult: (event: OperationProgressEvent) => R | null
  now?: () => number
  /** #1316 review round 1: the follow is work that must FINISH in the session that started it (an undo's job, D134 —
   *  its status reads name the pinned identity): a session end does not stop it. Default: bound to the session. */
  runsToCompletion?: boolean
}

export interface JobTrackerTimings {
  /** How often the tracker looks at the socket state. */
  tickMs?: number
  /** Status poll interval while the socket is down. */
  offlinePollMs?: number
  /** Silence on an open socket before one status check. */
  stallMs?: number
  /** Give up (outcome unknown) after this long. Past the server's marker age. */
  deadlineMs?: number
  /** A 404 from the status route this soon after `follow` started means "not visible yet", not "gone". */
  visibleGraceMs?: number
}

/** Past the server's marker age (an hour), so a live job is never given up on. Lives in
 *  `@papan/types` beside `JOB_PICKUP_MARGIN_MS`: the worker's pickup check reads the same value. */
export { JOB_DEADLINE_MS }

const DEFAULTS: Required<JobTrackerTimings> = {
  tickMs: 2_000,
  offlinePollMs: 4_000,
  stallMs: 30_000,
  deadlineMs: JOB_DEADLINE_MS,
  visibleGraceMs: 15_000,
}

export interface JobTracker<R> {
  /**
   * Follow the job the server queued. `operationId` is the one it answered
   * with — an earlier tab's when this request joined its job.
   */
  follow: (jobId: string, operationId: string, onProgress?: (p: JobProgress) => void) => Promise<R>
  /** Stop listening (the request answered inline, or the caller gave up). */
  dispose: () => void
}

export function createJobTracker<R>(
  deps: JobTrackerDeps<R>,
  ownOperationId: string,
  timings: JobTrackerTimings = {},
): JobTracker<R> {
  const t = { ...DEFAULTS, ...timings }
  const now = deps.now ?? Date.now
  // #1316: the follow belongs to the session that started it — once that session ended, no poll is asked again (it
  // would go out as the NEXT session)
  const start = deps.runsToCompletion ? undefined : currentSessionSignal()
  const buffered: OperationProgressEvent[] = []
  let onEvent: ((e: OperationProgressEvent) => void) | null = null
  const unsubscribe = deps.onOperationEvent((e) => {
    if (e.kind !== deps.kind) return
    if (onEvent) onEvent(e)
    else if (e.operationId === ownOperationId) buffered.push(e)
  })
  let disposed = false
  let timer: ReturnType<typeof setInterval> | null = null
  const dispose = () => {
    if (disposed) return
    disposed = true
    unsubscribe()
    if (timer) clearInterval(timer)
    timer = null
  }

  const follow: JobTracker<R>['follow'] = (jobId, operationId, onProgress) =>
    new Promise<R>((resolve, reject) => {
      if (disposed) { reject(new JobLostError()); return }
      const startedAt = now()
      let lastSignalAt = startedAt
      let lastPollAt = -Infinity
      let wasConnected = deps.isConnected()
      let polling = false
      let settled = false
      // When a `completed` event without its result arrived (null otherwise).
      let completedAt: number | null = null
      // A 404 came inside the visibility grace: ask again as soon as it is over.
      let recheckAfterGrace = false

      const finish = (fn: () => void) => {
        if (settled) return
        settled = true
        dispose()
        fn()
      }
      const handle = (e: OperationProgressEvent) => {
        if (e.jobId !== jobId && e.operationId !== operationId) return
        lastSignalAt = now()
        const result = e.stage === 'completed' ? deps.readResult(e) : null
        if (result !== null) {
          finish(() => resolve(result))
        } else if (e.stage === 'completed') {
          // Done, but the event does not carry the whole result (a split
          // delete's skipped names stay off the tenant-wide channel, #801):
          // the status route has it. The event goes out before BullMQ marks the
          // job completed, so the first answer may still say "running" — every
          // tick asks again until it is final (see the timer).
          completedAt ??= now()
          void poll()
        } else if (e.stage === 'failed') {
          finish(() => reject(new JobFailedError(e.error || 'The operation failed')))
        } else if (e.phase) {
          onProgress?.({ phase: e.phase, done: e.done ?? 0, total: e.total ?? 0 })
        }
      }
      const poll = async () => {
        if (polling || settled) return
        if (sessionEndedSince(start)) { finish(() => reject(new SessionEndedError())); return }
        polling = true
        lastPollAt = now()
        try {
          const s = await deps.getStatus(jobId)
          if (s.state === 'completed' && s.result) finish(() => resolve(s.result!))
          else if (s.state === 'failed') finish(() => reject(new JobFailedError(s.error || 'The operation failed')))
          else lastSignalAt = now() // alive: re-arm the stall check
        } catch (err) {
          // #1316: failed because its session ended — the follow ends here, quietly (callers ignore a SessionEndedError)
          if (sessionEndedSince(start, err)) {
            finish(() => reject(isSessionEndedError(err) ? err : new SessionEndedError()))
            return
          }
          // 404: the job is gone or not ours; nothing more will arrive — except right after the 202 (review round 3):
          // the server adds the job to BullMQ AFTER its transaction commits, without awaiting it (bin.ts
          // enqueueBinRestore; the folder delete enqueues the same way), so a poll asked at once — offline, or
          // following a joined job — can beat it there. In the first `visibleGraceMs` it is "not visible yet".
          if ((err as { status?: number })?.status === 404) {
            if (now() - startedAt < t.visibleGraceMs) {
              lastSignalAt = now()
              recheckAfterGrace = true // asked again once the grace is over — on a connected tab nothing else would ask until stallMs
            } else finish(() => reject(new JobLostError()))
          }
          // Anything else (network): try again on a later tick.
        } finally {
          polling = false
        }
      }

      onEvent = handle
      for (const e of buffered.splice(0)) handle(e)
      if (settled) return

      timer = setInterval(() => {
        const at = now()
        if (at - startedAt >= t.deadlineMs) { finish(() => reject(new JobLostError())); return }
        const connected = deps.isConnected()
        if (recheckAfterGrace && at - startedAt >= t.visibleGraceMs) {
          recheckAfterGrace = false
          void poll()
        }
        if (completedAt !== null) {
          // Finished, result not read yet: ask on every tick, but not forever —
          // past `stallMs` the outcome is unknown and the listing is the truth.
          if (at - completedAt >= t.stallMs) { finish(() => reject(new JobLostError())); return }
          void poll()
        } else if (!connected) {
          if (at - lastPollAt >= t.offlinePollMs) void poll()
        } else if (!wasConnected) {
          void poll() // back online: the outcome may have gone out in the gap
        } else if (at - lastSignalAt >= t.stallMs) {
          lastSignalAt = at
          void poll()
        }
        wasConnected = connected
      }, t.tickMs)

      // Following someone else's operation (a joined job, or a reload), or
      // starting offline: its outcome may already be out. Ask once, now.
      if (operationId !== ownOperationId || !wasConnected) void poll()
    })

  return { follow, dispose }
}

// ── Rows with a request in flight, per tab, outliving any one view ────────
//
// A view's own state is gone the moment the person navigates away, while the
// request it sent keeps going (and a big folder's request can take a while to
// answer). Coming back to the folder must still show the row as busy, so the
// set lives here, not in the view. Counted, so two runs over the same row (a
// retry, a second view) don't clear each other's mark.

export interface InFlightSet {
  /** Mark (`on`) or unmark ids. Every mark must be paired with an unmark. */
  mark: (ids: string[], on: boolean) => void
  subscribe: (fn: () => void) => () => void
  /** The same object until something changes (a `useSyncExternalStore` snapshot). */
  get: () => ReadonlySet<string>
}

export function createInFlightSet(): InFlightSet {
  const counts = new Map<string, number>()
  const listeners = new Set<() => void>()
  let snapshot: ReadonlySet<string> = new Set()
  return {
    mark(ids, on) {
      for (const id of ids) {
        const n = (counts.get(id) ?? 0) + (on ? 1 : -1)
        if (n > 0) counts.set(id, n)
        else counts.delete(id)
      }
      snapshot = new Set(counts.keys())
      for (const fn of listeners) fn()
    },
    subscribe(fn) {
      listeners.add(fn)
      return () => { listeners.delete(fn) }
    },
    get() {
      return snapshot
    },
  }
}

/**
 * E7 PR 0b — a restore (or any write) that met an app database whose check is STILL RUNNING server-side answers 503
 * `ENGINE_DB_VERIFYING`: nothing was done; the check carries on in the background and a later attempt finds it proven
 * (#1074). Owner, 2026-10-20 (option B): the client does NOT retry — the person is told to try again in a moment.
 */
export function isDbVerifyingError(err: unknown): boolean {
  return (err as { code?: unknown } | null)?.code === 'ENGINE_DB_VERIFYING'
}

/** The signal ONE request carries: its own timeout, and — when the caller passes the session's (`sessionSignal`) —
 *  that too, so the session ending aborts the request IN FLIGHT. */
export function requestSignal(timeoutMs: number, session?: AbortSignal): AbortSignal {
  const timeout = AbortSignal.timeout(timeoutMs)
  if (!session) return timeout
  if (typeof AbortSignal.any === 'function') return AbortSignal.any([timeout, session])
  const c = new AbortController()
  const end = (s: AbortSignal) => () => c.abort(s.reason)
  if (session.aborted) c.abort(session.reason)
  else { session.addEventListener('abort', end(session), { once: true }); timeout.addEventListener('abort', end(timeout), { once: true }) }
  return c.signal
}

/**
 * The request's answer never arrived, so the server may or may not have acted
 * on it: the client's own timeout, a dropped connection, or a gateway giving up
 * (502/503/504, Cloudflare 520–524) while the origin carried on.
 */
export function requestOutcomeUnknown(err: unknown): boolean {
  // a 503 "still verifying" is a KNOWN outcome — nothing was done (the person is told to try again)
  if (isDbVerifyingError(err)) return false
  // #1430 (2): an extract's refusal that names what it already wrote is the application's own answer (a busy database
  // is a 503 too) — resending it would extract a second time and lose the first run's undo
  if ((err as { partial?: unknown } | null)?.partial) return false
  // #1316: an answer dropped because its session ended (an `AbortError` by name) is not a lost answer — asking again
  // would re-send the write as the NEXT session's identity / tenant and show its answer there. (The marker read
  // directly, as api-client's `isSessionEndedError` does — no import, so the many partial `@papan/api-client` mocks
  // of this module's importers keep working.)
  if ((err as { sessionEnded?: unknown } | null)?.sessionEnded === true) return false
  const status = (err as { status?: number } | null)?.status
  if (typeof status === 'number') return status === 502 || status === 503 || status === 504 || (status >= 520 && status <= 524)
  const name = (err as { name?: string } | null)?.name
  return name === 'TimeoutError' || name === 'AbortError' || err instanceof TypeError
}

// ── Pending jobs survive a reload (sessionStorage, per tab) ───────────────

export interface PendingJob {
  jobId: string
  operationId: string
  startedAt: number
}

export interface PendingJobs<P extends PendingJob> {
  add: (p: P) => void
  remove: (jobId: string) => void
  /** The ones still worth following; drops (and forgets) any too old to matter. */
  list: (now?: number) => P[]
}

/** One sessionStorage list per kind, under `storageKey`. */
export function createPendingJobs<P extends PendingJob>(storageKey: string, maxAgeMs: number = JOB_DEADLINE_MS): PendingJobs<P> {
  const read = (): P[] => {
    try {
      const raw = sessionStorage.getItem(storageKey)
      const list = raw ? (JSON.parse(raw) as P[]) : []
      return Array.isArray(list) ? list : []
    } catch {
      return []
    }
  }
  const write = (list: P[]): void => {
    try {
      if (list.length === 0) sessionStorage.removeItem(storageKey)
      else sessionStorage.setItem(storageKey, JSON.stringify(list))
    } catch { /* private mode / quota: a reload then just loses the toast */ }
  }
  return {
    add(p) {
      write([...read().filter(x => x.jobId !== p.jobId), p])
    },
    remove(jobId) {
      write(read().filter(x => x.jobId !== jobId))
    },
    list(now = Date.now()) {
      const all = read()
      const live = all.filter(x => now - x.startedAt < maxAgeMs)
      if (live.length !== all.length) write(live)
      return live
    },
  }
}
